import dayjs from "dayjs";
import { sessionOverlapsCompanyBlock } from "./companyBlockDates";

export const CLEANER_NOT_AVAILABLE_MESSAGE =
  "No cleaners are available for your selected time.";

export const PREFERRED_CLEANER_NOT_AVAILABLE_MESSAGE =
  "Your preferred cleaner is not available for the selected schedule.";

export const PREFERRED_CLEANER_BLOCKED_ON_DATE_MESSAGE =
  "Selection of preferred cleaner is not available on this date";

export const COMPANY_BLOCK_MESSAGE =
  "The selected time slot is not available. Please choose another time.";

/** Admin company block on the session date — booking cannot proceed. */
export const ADMIN_BLOCK_PERIOD_MESSAGE =
  "Bookings are not available during this period";

export const UNABLE_TO_VERIFY_CLEANER_MESSAGE =
  "Unable to verify cleaner availability.";

export const PREFERRED_CLEANER_REPLACEMENT_NOTE =
  "*Due to unavailbility, preferred cleaner will be replace by another cleaner.";

const SINGAPORE_TZ = "Asia/Singapore";
const BROAD_SESSION_MS = 8 * 60 * 60 * 1000;

const getSingaporeDayBounds = (dateInput) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: SINGAPORE_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const dateKey = formatter.format(new Date(dateInput));
  return {
    dayStart: new Date(`${dateKey}T00:00:00+08:00`),
    dayEnd: new Date(`${dateKey}T23:59:59.999+08:00`),
  };
};

const blockCoversFullDay = (blockStart, blockEnd) => {
  const { dayStart, dayEnd } = getSingaporeDayBounds(blockStart);
  return (
    new Date(blockStart) <= dayStart && new Date(blockEnd) >= dayEnd
  );
};

/** Matches backend: date chosen but concrete session time not final yet. */
export function isBroadSessionWindow(sessionStart, sessionEnd) {
  if (!sessionStart || !sessionEnd) {
    return false;
  }
  if (blockCoversFullDay(sessionStart, sessionEnd)) {
    return true;
  }
  const durationMs =
    new Date(sessionEnd).getTime() - new Date(sessionStart).getTime();
  return durationMs >= BROAD_SESSION_MS;
}

export function isSessionAvailabilityResponse(availability = []) {
  return availability.some(
    (item) =>
      item?.sessionNo !== undefined ||
      item?.preferredCleanerSelectionBlocked !== undefined
  );
}

export function hasPreferredCleanerSelectionBlocked(availability = []) {
  return availability.some(
    (item) =>
      (item?.blockType === "company" || item?.blockType === "staff") &&
      (item?.preferredCleanerSelectionBlocked === true ||
        item?.clearPreferredStaff === true)
  );
}

/** Clear preferred picker when backend marks the date blocked for preferred selection. */
export function shouldClearPreferredStaff(availability = []) {
  return hasPreferredCleanerSelectionBlocked(availability);
}

/** Per-session preferred check from regular-staff-availability. */
export function normalizeRegularSessionUnavailableReason(reason) {
  if (
    reason === COMPANY_BLOCK_MESSAGE ||
    reason === PREFERRED_CLEANER_BLOCKED_ON_DATE_MESSAGE
  ) {
    return ADMIN_BLOCK_PERIOD_MESSAGE;
  }
  return reason;
}

export function getPreferredSessionAvailabilityState(availability = []) {
  if (!availability.length || !isSessionAvailabilityResponse(availability)) {
    return {
      isSessionAvailability: false,
      anyUnavailable: false,
      allUnavailable: false,
      partialUnavailable: false,
      hasCompanyBlock: false,
    };
  }

  const unavailableSessions = availability.filter(
    (item) => item?.isAvailable === false
  );
  const availableSessions = availability.filter(
    (item) => item?.isAvailable !== false
  );
  const hasCompanyBlock = unavailableSessions.some(
    (item) =>
      item?.blockType === "company" ||
      item?.unavailabilityReason === COMPANY_BLOCK_MESSAGE
  );

  return {
    isSessionAvailability: true,
    anyUnavailable: unavailableSessions.length > 0,
    allUnavailable:
      unavailableSessions.length === availability.length &&
      availability.length > 0,
    partialUnavailable:
      unavailableSessions.length > 0 && availableSessions.length > 0,
    hasCompanyBlock,
    unavailableSessions,
    availableSessions,
  };
}

/**
 * Selected-schedule red highlight: company blocks and API unavailability must
 * show even when preferred staff was cleared after preferredCleanerSelectionBlocked.
 */
export function getRegularSessionDisplayState(
  sessionAvail,
  session,
  companyBlockDates = []
) {
  const startDate = session?.startDate;
  const endDate = session?.endDate;

  const overlapsCompanyBlock =
    startDate &&
    endDate &&
    sessionOverlapsCompanyBlock(
      startDate,
      endDate,
      dayjs(startDate),
      companyBlockDates
    );

  if (overlapsCompanyBlock) {
    return {
      unavailable: true,
      reason: ADMIN_BLOCK_PERIOD_MESSAGE,
    };
  }

  if (sessionAvail?.preferredCleanerSelectionBlocked) {
    return {
      unavailable: true,
      reason: ADMIN_BLOCK_PERIOD_MESSAGE,
    };
  }

  if (sessionAvail?.isAvailable === false) {
    const reason =
      sessionAvail?.unavailabilityReason ||
      (sessionAvail?.blockType === "company"
        ? ADMIN_BLOCK_PERIOD_MESSAGE
        : CLEANER_NOT_AVAILABLE_MESSAGE);
    return {
      unavailable: true,
      reason: normalizeRegularSessionUnavailableReason(reason),
    };
  }

  return { unavailable: false, reason: null };
}

export function hasUnavailableRegularSessionForDisplay(
  regularServiceArrays = [],
  staffAvailabilityArrayForRegular = [],
  companyBlockDates = []
) {
  return regularServiceArrays.some((session, index) =>
    getRegularSessionDisplayState(
      staffAvailabilityArrayForRegular[index],
      session,
      companyBlockDates
    ).unavailable
  );
}

export function shouldShowPreferredReplacementNote(
  availability = [],
  { selectedPreferredStaff = [] } = {}
) {
  if (!selectedPreferredStaff?.length) return false;
  if (hasPreferredCleanerSelectionBlocked(availability)) return false;
  const state = getPreferredSessionAvailabilityState(availability);
  return state.anyUnavailable && state.isSessionAvailability;
}

/** Stable identity for regular booking availability vs current schedule context. */
export function buildRegularStaffAvailabilityScheduleKey({
  regularServiceArrays = [],
  selectedPreferredStaff = [],
  dateOnlyPreferredStaffCheck = false,
} = {}) {
  if (!regularServiceArrays?.length) {
    return "";
  }

  const useDateOnlyCheck =
    dateOnlyPreferredStaffCheck ||
    regularServiceArrays.every((session) =>
      isBroadSessionWindow(session.startDate, session.endDate)
    );

  const sessions = regularServiceArrays.map((session) => ({
    startDate: session.startDate,
    endDate: session.endDate,
    duration: session.duration,
    position: session.position,
  }));
  const preferred = [...selectedPreferredStaff].sort();

  return JSON.stringify({
    dateOnly: useDateOnlyCheck,
    preferred,
    sessions,
  });
}

export function buildStaffAvailabilityRequest({
  selectedCategory,
  selectedAddress,
  selectedPreferredStaff,
  selectedStartDate,
  selectedEndDate,
  selectedService,
  selectedDuration,
  regularServiceArrays,
  dateOnlyPreferredStaffCheck = false,
  createdFor = null,
}) {
  const category = selectedCategory?.toLowerCase();

  if (category === "regular") {
    if (!regularServiceArrays?.length) return null;

    const useDateOnlyCheck =
      dateOnlyPreferredStaffCheck ||
      regularServiceArrays.every((session) =>
        isBroadSessionWindow(session.startDate, session.endDate)
      );

    return {
      location: selectedAddress?.location,
      orderType: "Regular",
      svcStartDate: regularServiceArrays[0]?.startDate,
      svcEndDate:
        regularServiceArrays[regularServiceArrays.length - 1]?.endDate,
      staffNeeded: 1,
      preferredStaffs: selectedPreferredStaff,
      serviceArray: regularServiceArrays,
      dateOnlyPreferredStaffCheck: useDateOnlyCheck,
      ...(createdFor ? { createdFor } : {}),
    };
  }

  if (category === "general") {
    if (!selectedStartDate || !selectedEndDate || !selectedService?.length) {
      return null;
    }

    return {
      location: selectedAddress?.location,
      orderType: "General",
      svcStartDate: selectedStartDate,
      svcEndDate: selectedEndDate,
      staffNeeded: 1,
      preferredStaffs: [],
      serviceArray: selectedService.map((item, index) => ({
        serviceId: item?._id,
        duration: selectedDuration,
        position: index + 1,
        staff: "",
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      })),
    };
  }

  return null;
}

export function getRegularBookingProceedIssue({
  regularServiceArrays = [],
  staffAvailabilityArrayForRegular = [],
  companyBlockDates = [],
  selectedPreferredStaff = [],
} = {}) {
  if (
    hasUnavailableRegularSessionForDisplay(
      regularServiceArrays,
      staffAvailabilityArrayForRegular,
      companyBlockDates
    )
  ) {
    for (let index = 0; index < regularServiceArrays.length; index += 1) {
      const display = getRegularSessionDisplayState(
        staffAvailabilityArrayForRegular[index],
        regularServiceArrays[index],
        companyBlockDates
      );
      if (display.unavailable && display.reason) {
        return display.reason;
      }
    }
    return ADMIN_BLOCK_PERIOD_MESSAGE;
  }

  return getCleanerAvailabilityIssue(staffAvailabilityArrayForRegular, {
    selectedPreferredStaff,
  });
}

export function getCleanerAvailabilityIssue(
  availability = [],
  { selectedPreferredStaff = [], secondaryStaff = "" } = {}
) {
  if (!availability.length) {
    return null;
  }

  const isSessionAvailability = isSessionAvailabilityResponse(availability);

  if (isSessionAvailability) {
    const sessionState = getPreferredSessionAvailabilityState(availability);

    if (
      selectedPreferredStaff?.length &&
      hasPreferredCleanerSelectionBlocked(availability)
    ) {
      return ADMIN_BLOCK_PERIOD_MESSAGE;
    }

    // Preferred staff may be unavailable on some sessions; order creation assigns another cleaner.
    if (selectedPreferredStaff?.length && sessionState.anyUnavailable) {
      return null;
    }

    if (sessionState.allUnavailable) {
      const firstUnavailable = sessionState.unavailableSessions[0];
      if (firstUnavailable?.blockType === "company") {
        return ADMIN_BLOCK_PERIOD_MESSAGE;
      }
      if (firstUnavailable?.unavailabilityReason) {
        return normalizeRegularSessionUnavailableReason(
          firstUnavailable.unavailabilityReason
        );
      }
      return CLEANER_NOT_AVAILABLE_MESSAGE;
    }

    // Mixed or all-available session rows: show per-session copy on Selected Schedule.
    if (sessionState.partialUnavailable) {
      return null;
    }

    if (
      sessionState.availableSessions.length === availability.length &&
      availability.length > 0
    ) {
      return null;
    }

    if (!selectedPreferredStaff?.length && !secondaryStaff) {
      const hasAssignableStaff = availability.some(
        (item) => item?.user?._id || item?.isAvailable === true
      );
      if (!hasAssignableStaff) {
        return CLEANER_NOT_AVAILABLE_MESSAGE;
      }
    }

    return null;
  }

  if (!selectedPreferredStaff?.length && !secondaryStaff) {
    const staffId = availability[0]?.user?._id;
    if (!staffId) {
      return CLEANER_NOT_AVAILABLE_MESSAGE;
    }
  }

  return null;
}
